import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridPaginationModel, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { oemTenantId } from 'app/config';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPaginationGridData, selectPaginationMode, setPaginationGridData, setPaginationModel } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { USER_TENANT_MEMBERSHIPS_MAX_ENTITIES_NUMBER_PER_REQUEST } from 'common/constants/exportData';
import { DISPLAYED_GRID_ROWS_COUNT } from 'common/constants/pagination';
import { PaginationMode } from 'common/constants/paginationMode';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchPaginatedEntities } from 'common/hooks/usePalmFetchPaginatedEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchAllFilteredSortedTenantUserMemberships, fetchTenantUserPaginatedMemberships } from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { ActionDefinition, ColumnDefinition, Operations } from 'common/types/TypeDefs';
import { userMembershipColumnsDefinitionWithActions } from 'common/utils/userMembershipColumnsHelper';
import { selectGlobalRolesByTenantId } from 'features/globalRoles/globalRolesSlice';

import { useDropDownFiltersConfiguration } from './helpers';
import { TenantUserMembershipCreateContent } from './TenantUserMembershipCreateContent';
import { TenantUserMembershipDeleteDialogContent } from './tenantUserMembershipDeleteDialogContent';
import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilteredRoles,
    selectFilteredStatus,
    selectFilters } from './tenantUserMembershipSlice';

export function TenantUserMembershipTab() {
    const shouldDisableButtonsForDelete = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableButtonsForCreateAndEdit = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.GlobalRoleManager ]);
    const shouldDisableButtonsForOpen = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.GlobalRoleManager, Roles.MultiTenantRO, Roles.UserManager ]);

    const paginationGridData = useAppSelector(selectPaginationGridData);
    const filteredRoles = useAppSelector(selectFilteredRoles);
    const filteredStatus = useAppSelector(selectFilteredStatus);
    const filters = useAppSelector(selectFilters);
    const [processing, setProcessing] = useState<boolean>(false);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Server;
    const dispatch = useAppDispatch();
    const [sortModel, setSortModel] = usePalmSortModel('principalName');
    const [defaultSortModel] = usePalmSortModel('principalName');

    const { tenantId = '', suiteId } = useParams();
    const globalRoles = useAppSelector(selectGlobalRolesByTenantId(oemTenantId === tenantId));

    const fetchGridData = usePalmFetchPaginatedEntities(
        'User',
        { tenantId, globalRoles: filteredRoles, status: filteredStatus, skip: 0, take: 0 },
        fetchTenantUserPaginatedMemberships,
        defaultSortModel,
        setProcessing,
        selectFilters,
        [filters]
    );

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters,
        useDropDownFiltersConfiguration(globalRoles)
    );

    const fetchExportData = async (signal: AbortSignal, search?: string, model?: GridSortModel): Promise<GridRowModel[]> =>{
        return await dispatch(fetchAllFilteredSortedTenantUserMemberships({
            tenantId: tenantId ?? '',
            search: search ?? '',
            globalRoles: filteredRoles,
            status: filteredStatus,
            sortKey: model?.[0]?.field,
            sortOrder: model?.[0]?.sort,
            rowsCount: paginationGridData?.rowsCount,
            signal: signal,
            excludeDetails: true,
            maxNumberOfEntitiesPerRequest: USER_TENANT_MEMBERSHIPS_MAX_ENTITIES_NUMBER_PER_REQUEST
        }))
            .unwrap()
            .then((response) => {
                return response;
            });
    };

    const handlePageChange = (paginationModel: GridPaginationModel) => {
        dispatch(setPaginationModel(paginationModel));
        fetchGridData(paginationModel.page, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handleSortModelChange = (model: GridSortModel) => {
        if(!paginationGridData?.sortModel?.length && !model?.length) {
            return;
        }
        dispatch(setPaginationGridData({
            ...paginationGridData,
            sortModel: model,
        }));
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], model);
    };

    const refreshDataGrid = () => {
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const onGetRowLink = useCallback((id: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Users}/${id}/${Routes.Roles}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableButtonsForOpen, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableButtonsForCreateAndEdit },
        { action: Operations.Delete, disabled: shouldDisableButtonsForDelete }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = userMembershipColumnsDefinitionWithActions;

    return <PalmDataGrid
        title='User'
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        filter={filterProp}
        isCreateButtonDisabled={shouldDisableButtonsForCreateAndEdit}
        deleteFunction={TenantUserMembershipDeleteDialogContent}
        contentFunction={TenantUserMembershipCreateContent}
        operationName={Operations.Link}
        getRowLink={shouldDisableButtonsForOpen ? undefined : onGetRowLink}
        paginationMode={ paginationMode }
        searchFilterDisabled={filters.keywords.length !== 0}
        paginationInfo={{
            handlePageChange,
            handleSortModelChange,
            take: DISPLAYED_GRID_ROWS_COUNT,
        }}
        refreshDataGridClick={refreshDataGrid}
        sortModel={paginationMode == PaginationMode.Client ? sortModel: paginationGridData?.sortModel ?? defaultSortModel}
        shouldShowExportButton={true}
        fetchExportData={fetchExportData}
        clientSortHandler={setSortModel}
    />;
}
